import React, { useMemo, useState } from 'react';
import { Box, InputAdornment, Typography } from '@mui/material';
import { CustomButton, TextField } from '../../../../v2/MUI_custom_component';
import CardHeader from '../../Common/CardHeader';
import { cardStyles } from '../../Common/constants';

import { ProfilerQuestionID } from '../../config/QuestionEnum';
import CustomChip from '../../../Components/CustomChip';
import { alphaNumericInputValidation } from '../../../Common/Validation/alphaNumeric';
import IconReusable from '../../../../components/IconReusable';

const MAX_BRANDS = 5;
const MAX_BRAND_LENGTH = 30;

const BrandName = ({
  questionId,
  title,
  titleHelperText = '(Add 1 or more)',
  subtitle = 'This helps us give you more Social Security Benefits',
  placeholder = 'Enter brand name',
  submitLabel = 'Submit',
  value,
  defaultValue = [],
  onChange,
  onSubmit,
  maxBrands = MAX_BRANDS,
  suggestions = [],
  icon = 'https://d2djimvzddgguw.cloudfront.net/humbee_profiler/images/shilpkar.png',
}) => {
  const isControlled = Array.isArray(value);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const brandValues = isControlled ? value : internalValue;
  const normalizedBrands = useMemo(
    () => brandValues.map((item) => item.trim().toLowerCase()),
    [brandValues],
  );

  const matchedSuggestions = inputValue.trim()
    ? suggestions.filter(
        (s) =>
          s.toLowerCase().includes(inputValue.trim().toLowerCase()) &&
          !normalizedBrands.includes(s.toLowerCase()),
      )
    : [];

  const SUGGESTION_ITEM_HEIGHT = 36;
  const VISIBLE_SUGGESTIONS = 4;

  const updateBrands = (nextValues) => {
    if (!isControlled) {
      setInternalValue(nextValues);
    }
    onChange?.(nextValues);
  };

  const addBrand = (rawValue = inputValue) => {
    const trimmedValue = rawValue.trim();
    if (!trimmedValue || brandValues.length >= maxBrands) {
      return;
    }

    if (normalizedBrands.includes(trimmedValue.toLowerCase())) {
      setInputValue('');
      setShowSuggestions(false);
      return;
    }

    updateBrands([...brandValues, trimmedValue]);
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeBrand = (brandName) => {
    updateBrands(brandValues.filter((item) => item !== brandName));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addBrand();
    }
  };

  const handleSubmit = () => {
    onSubmit?.({
      question: [
        { id: questionId, value: brandValues }
      ]
    });
  };

  return (
    <Box sx={cardStyles({ overflow: 'hidden' })}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2,}}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
            <Box 
              component='img'
              alt='Brand Icon'
              width={64}
              height={64}
              src={icon}
            />
            <CardHeader
              alignItems='center'
              title={title}
              titleHelperText={titleHelperText}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, position: 'relative' }}>
            <TextField
              variant='standard'
              placeholder={placeholder}
              value={inputValue}

              onChange={(event) => {
                setInputValue(alphaNumericInputValidation(event.target.value).slice(0, MAX_BRAND_LENGTH));
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              onKeyDown={handleKeyDown}
              inputProps={{ maxLength: MAX_BRAND_LENGTH }}
              disabled={brandValues.length >= maxBrands}
              helperText={
                brandValues.length >= maxBrands
                  ? `Maximum ${maxBrands} brands allowed`
                  : !brandValues.length && suggestions.length > 0
                  ? `Eg: ${suggestions.slice(0, 2).join(', ')}`
                  : undefined
              }
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CustomButton
                        label="Add"
                        variant="text"
                        size="small"
                        disabled={brandValues.length >= maxBrands}
                        leftIcon={() => <IconReusable name="Add" size={20} />}
                        onClick={() => addBrand()}
                      />
                    </InputAdornment>
                  ),
                },
              }}
              error={brandValues.length >= maxBrands}
            />
            {showSuggestions && matchedSuggestions.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  mt: 0.6,
                  bgcolor: '#fff',
                  borderRadius: 1,
                  boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
                  maxHeight: SUGGESTION_ITEM_HEIGHT * VISIBLE_SUGGESTIONS,
                  overflowY: 'auto',
                }}>
                {matchedSuggestions.map((suggestion) => (
                  <Box
                    key={suggestion}
                    onMouseDown={() => addBrand(suggestion)}
                    sx={{
                      height: SUGGESTION_ITEM_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      fontSize: 14,
                      '&:hover': { bgcolor: '#F5F5F5' },
                    }}>
                    {suggestion}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 120, overflowY: 'auto' }}>
            {brandValues.map((brandName) => (
              <CustomChip
                key={brandName}
                label={brandName}
                selected
                onClick={() => removeBrand(brandName)}
                onDelete={() => removeBrand(brandName)}
                sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', wordBreak: 'break-word' } }}
              />
            ))}
          </Box>
        </Box>

        <CustomButton
          size='small'
          label={submitLabel}
          onClick={handleSubmit}
          disabled={!brandValues.length}
          sx={{ width: '100%' }}
        />
      </Box>
    </Box>
  );
};

export default BrandName;
